import type { ImagePayload } from '@nk/shared';
import { ResponsiveImage } from '@/media/ResponsiveImage';
import { cn } from '@/components/ui';

/**
 * Bridges a Phase 6 `ImagePayload` to Phase 4's `<ResponsiveImage>`.
 *
 * The payload was shaped to need no adapter beyond this: its variants already carry the
 * `storagePath` the component expects. What this adds is the *policy* — only the hero
 * loads eagerly (Phase 8 task list: "above-the-fold hero image preloaded; everything
 * below the fold lazy-loaded"), and a story with no photograph gets a placeholder rather
 * than a grey box.
 */
export function ArticleImage({
  image,
  alt,
  sizes,
  priority = false,
  className,
  aspect = 'aspect-[3/2]',
  placeholderSeed,
}: {
  image: ImagePayload | null;
  /** Falls back to the media row's own alt text; pass a headline only when that is empty. */
  alt?: string;
  sizes?: string;
  priority?: boolean;
  className?: string;
  aspect?: string;
  /**
   * Anything stable about the story — its category slug is the useful choice — so the
   * placeholder's tint is the same on every render and every device, and a category's
   * imageless stories look like a set rather than an accident.
   */
  placeholderSeed?: string;
}) {
  if (!image) {
    return (
      <BrandPlaceholder seed={placeholderSeed ?? alt ?? ''} aspect={aspect} className={className} />
    );
  }

  return (
    <ResponsiveImage
      variants={image.variants}
      fallbackStoragePath={image.storagePath}
      width={image.width}
      height={image.height}
      alt={image.alt || alt || ''}
      blurhash={image.blurhash}
      sizes={sizes ?? '(max-width: 720px) 100vw, 33vw'}
      loading={priority ? 'eager' : 'lazy'}
      className={className}
    />
  );
}

/**
 * What a card shows when the story has no picture.
 *
 * A local desk files copy faster than it files photographs, so this is not an edge case —
 * it is Tuesday. The previous behaviour was an empty grey box, which reserved the space
 * correctly (no layout shift) and made a finished story look like a broken one.
 *
 * Drawn rather than fetched: an inline SVG costs no request, scales to any card, and is
 * the same in the SSR pass as after hydration. The tint is derived from the seed so the
 * page is not a wall of identical grey, and it is held at a light, low-saturation value
 * so white cards and dark bands both keep their contrast — the mark is decoration, and
 * the headline beside it carries the meaning, so the whole thing is `aria-hidden`.
 */
function BrandPlaceholder({
  seed,
  aspect,
  className,
}: {
  seed: string;
  aspect: string;
  className?: string;
}) {
  const hue = hashHue(seed);

  return (
    <div
      aria-hidden="true"
      data-placeholder="brand"
      className={cn('grid place-items-center overflow-hidden', aspect, className)}
      style={{
        background: `linear-gradient(135deg, hsl(${hue} 24% 94%) 0%, hsl(${hue} 18% 88%) 100%)`,
      }}
    >
      <svg
        viewBox="0 0 120 56"
        width="88"
        height="41"
        role="presentation"
        focusable="false"
        className="max-w-[45%]"
      >
        <text
          x="60"
          y="30"
          textAnchor="middle"
          fontSize="30"
          fontWeight="800"
          letterSpacing="-1.5"
          fill={`hsl(${hue} 20% 62%)`}
        >
          NK
        </text>
        <rect x="34" y="38" width="52" height="3" rx="1.5" fill={`hsl(${hue} 20% 70%)`} />
      </svg>
    </div>
  );
}

/** FNV-1a, the hash the seed script already uses, folded onto the colour wheel. */
function hashHue(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % 360;
}
